import { useMutation } from "@tanstack/react-query";
import { AlertCircle } from "lucide-react";
import { useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../api/client";

const OAUTH_STATE_KEY = "moras_gateway_oauth_state";
const OAUTH_RETURN_TO_KEY = "moras_gateway_oauth_return_to";

// OAuthCallbackPage validates browser state before asking Moneyking to exchange the code.
// 回调页先校验浏览器状态，再让 Moneyking 后端交换授权码。
export function OAuthCallbackPage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const started = useRef(false);
  const exchange = useMutation({
    mutationFn: (code: string) => api.oauthExchange(code),
    onSuccess: () => {
      const returnTo = sessionStorage.getItem(OAUTH_RETURN_TO_KEY);
      sessionStorage.removeItem(OAUTH_RETURN_TO_KEY);
      navigate(returnTo?.startsWith("/manage/") ? returnTo : "/manage/overview", { replace: true });
    },
  });

  const code = search.get("code") || "";
  const state = search.get("state") || "";
  const expectedState = sessionStorage.getItem(OAUTH_STATE_KEY) || "";
  const providerError = search.get("error");
  const stateValid = Boolean(state && expectedState && state === expectedState);

  useEffect(() => {
    if (started.current || providerError || !code || !stateValid) return;
    started.current = true;
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    exchange.mutate(code);
  }, [code, exchange, providerError, stateValid]);

  const message = providerError
    ? "Keeper 未授权本次登录。"
    : !code
      ? "Keeper 没有返回授权码。"
      : !stateValid
        ? "登录状态校验失败，请重新发起登录。"
        : exchange.isError
          ? exchange.error.message
          : "正在完成 Keeper 登录";

  const failed = Boolean(providerError || !code || !stateValid || exchange.isError);
  return (
    <main className="oauth-callback-page">
      <section className={`oauth-callback-panel ${failed ? "is-error" : ""}`}>
        {failed ? <AlertCircle size={28} /> : <div className="oauth-progress" aria-hidden="true" />}
        <div>
          <h1>{failed ? "登录失败" : "正在登录"}</h1>
          <p>{message}</p>
        </div>
        {failed ? <Link className="primary-button" to="/login">重新登录</Link> : null}
      </section>
    </main>
  );
}

export const oauthStorageKeys = { state: OAUTH_STATE_KEY, returnTo: OAUTH_RETURN_TO_KEY };
